const CAP_BITCOIN_SCRIPT = {
  id: 280,
  title: "Bitcoin Script",
  slug: "bitcoin-script",
  type: "lesson",
  category: "bitcoin-avanzado",
  level: "advanced",
  preview:
    "Bitcoin Script es el lenguaje de programación sencillo y no Turing-completo que define las condiciones bajo las cuales se pueden gastar los bitcoins, usando una pila y códigos de operación (OP_CODES).",
  sections: [
    {
      heading: "¿Qué es Bitcoin Script?",
      text: [
        "Bitcoin Script es un lenguaje de programación simple empleado en Bitcoin para el procesamiento de las transacciones que se lee de izquierda a derecha. Está basado en una serie de estructuras lineales, conocidas como pila (stack), que contienen datos existentes en orden LIFO (Last In – First Out). Cada instrucción en este lenguaje se ejecuta consecutivamente una después de la otra.",
        "El propósito de la creación de un lenguaje script en Bitcoin, es el de proporcionar una serie de parámetros fáciles y flexibles para poder posibilitar una transacción. Así, cuando Satoshi Nakamoto desarrolló Bitcoin, deshabilitó varias funciones, entre ellas la multiplicación. Por lo que el script se mantiene simple en términos de programación. Entonces, este lenguaje de programación es lo que determina si una operación puede realizarse o no. Es decir, si dicha operación está autorizada para la transferencia de fondos.",
        "En pocas palabras, la creación del Bitcoin Script es lo que otorga la naturaleza programable de Bitcoin. Es lo que nos permite, por ejemplo, enviar una transacción y que la misma se valide solo si determinadas condiciones se cumplen. Esta propiedad es algo único de Bitcoin y las criptomonedas, algo que el dinero digital actual no puede alcanzar, y mucho menos el arcaico dinero físico. Es la evolución del dinero, llegado a un punto en que incluso este puede actuar de forma independiente y descentralizada atendiendo simplemente a la programación incrustada en el mismo."
      ]
    },

    {
      heading: "Limitaciones intencionales del lenguaje",
      text: [
        "Este lenguaje no es Turing Completo debido a que su funcionalidad es limitada y no puede realizar bucles. Por lo que no es capaz de resolver cualquier tipo de problema como las máquinas de Turing.",
        "No obstante, esta limitación es intencional, ya que así se evita la entrada en un bucle infinito y la ejecución de errores. De esta forma, si existieran actores maliciosos dentro del programa, no podrían tener la libertad de crear operaciones complicadas para consumir la tasa de hash y ralentizar el sistema de Bitcoin a través de bucles infinitos."
      ]
    },

    {
      heading: "¿Por qué hacen falta los OP_CODES?",
      text: [
        "Un lenguaje de programación es necesario debido a que nos permite escribir programas y que los ordenadores ejecuten nuestros deseos. En Bitcoin, para comunicar esos deseos son necesarios los códigos de operación (OP_CODES), que sirven para varias funciones, como la manipulación de memoria, operaciones matemáticas, control de flujo, llamadas a funciones, entre muchas otras.",
        "Por ello, Bitcoin Script es en esencia un conjunto de instrucciones programadas que se registran con cada transacción realizada. Estas instrucciones describen cómo los usuarios pueden obtener acceso a los bitcoins disponibles en la red y hacer uso de ellos."
      ]
    },

    {
      heading: "Listado de OP_CODES disponibles en Bitcoin Script",
      text: [
        "A continuación se muestra un listado clásico de OP_CODES de Bitcoin Script, organizado por categorías tal como aparece en la referencia técnica:",
        "/** Script opcodes */",
        "enum opcodetype",
        "{",
        "  // push value",
        "  OP_0 = 0x00,",
        "  OP_FALSE = OP_0,",
        "  OP_PUSHDATA1 = 0x4c,",
        "  OP_PUSHDATA2 = 0x4d,",
        "  OP_PUSHDATA4 = 0x4e,",
        "  OP_1NEGATE = 0x4f,",
        "  OP_RESERVED = 0x50,",
        "  OP_1 = 0x51,",
        "  OP_TRUE = OP_1,",
        "  OP_2 = 0x52,",
        "  OP_3 = 0x53,",
        "  OP_4 = 0x54,",
        "  OP_5 = 0x55,",
        "  OP_6 = 0x56,",
        "  OP_7 = 0x57,",
        "  OP_8 = 0x58,",
        "  OP_9 = 0x59,",
        "  OP_10 = 0x5a,",
        "  OP_11 = 0x5b,",
        "  OP_12 = 0x5c,",
        "  OP_13 = 0x5d,",
        "  OP_14 = 0x5e,",
        "  OP_15 = 0x5f,",
        "  OP_16 = 0x60,",
        "",
        "  // control",
        "  OP_NOP = 0x61,",
        "  OP_VER = 0x62,",
        "  OP_IF = 0x63,",
        "  OP_NOTIF = 0x64,",
        "  OP_VERIF = 0x65,",
        "  OP_VERNOTIF = 0x66,",
        "  OP_ELSE = 0x67,",
        "  OP_ENDIF = 0x68,",
        "  OP_VERIFY = 0x69,",
        "  OP_RETURN = 0x6a,",
        "",
        "  // stack ops",
        "  OP_TOALTSTACK = 0x6b,",
        "  OP_FROMALTSTACK = 0x6c,",
        "  OP_2DROP = 0x6d,",
        "  OP_2DUP = 0x6e,",
        "  OP_3DUP = 0x6f,",
        "  OP_2OVER = 0x70,",
        "  OP_2ROT = 0x71,",
        "  OP_2SWAP = 0x72,",
        "  OP_IFDUP = 0x73,",
        "  OP_DEPTH = 0x74,",
        "  OP_DROP = 0x75,",
        "  OP_DUP = 0x76,",
        "  OP_NIP = 0x77,",
        "  OP_OVER = 0x78,",
        "  OP_PICK = 0x79,",
        "  OP_ROLL = 0x7a,",
        "  OP_ROT = 0x7b,",
        "  OP_SWAP = 0x7c,",
        "  OP_TUCK = 0x7d,",
        "",
        "  // splice ops",
        "  OP_CAT = 0x7e,",
        "  OP_SUBSTR = 0x7f,",
        "  OP_LEFT = 0x80,",
        "  OP_RIGHT = 0x81,",
        "  OP_SIZE = 0x82,",
        "",
        "  // bit logic",
        "  OP_INVERT = 0x83,",
        "  OP_AND = 0x84,",
        "  OP_OR = 0x85,",
        "  OP_XOR = 0x86,",
        "  OP_EQUAL = 0x87,",
        "  OP_EQUALVERIFY = 0x88,",
        "  OP_RESERVED1 = 0x89,",
        "  OP_RESERVED2 = 0x8a,",
        "",
        "  // numeric",
        "  OP_1ADD = 0x8b,",
        "  OP_1SUB = 0x8c,",
        "  OP_2MUL = 0x8d,",
        "  OP_2DIV = 0x8e,",
        "  OP_NEGATE = 0x8f,",
        "  OP_ABS = 0x90,",
        "  OP_NOT = 0x91,",
        "  OP_0NOTEQUAL = 0x92,",
        "",
        "  OP_ADD = 0x93,",
        "  OP_SUB = 0x94,",
        "  OP_MUL = 0x95,",
        "  OP_DIV = 0x96,",
        "  OP_MOD = 0x97,",
        "  OP_LSHIFT = 0x98,",
        "  OP_RSHIFT = 0x99,",
        "",
        "  OP_BOOLAND = 0x9a,",
        "  OP_BOOLOR = 0x9b,",
        "  OP_NUMEQUAL = 0x9c,",
        "  OP_NUMEQUALVERIFY = 0x9d,",
        "  OP_NUMNOTEQUAL = 0x9e,",
        "  OP_LESSTHAN = 0x9f,",
        "  OP_GREATERTHAN = 0xa0,",
        "  OP_LESSTHANOREQUAL = 0xa1,",
        "  OP_GREATERTHANOREQUAL = 0xa2,",
        "  OP_MIN = 0xa3,",
        "  OP_MAX = 0xa4,",
        "",
        "  OP_WITHIN = 0xa5,",
        "",
        "  // crypto",
        "  OP_RIPEMD160 = 0xa6,",
        "  OP_SHA1 = 0xa7,",
        "  OP_SHA256 = 0xa8,",
        "  OP_HASH160 = 0xa9,",
        "  OP_HASH256 = 0xaa,",
        "  OP_CODESEPARATOR = 0xab,",
        "  OP_CHECKSIG = 0xac,",
        "  OP_CHECKSIGVERIFY = 0xad,",
        "  OP_CHECKMULTISIG = 0xae,",
        "  OP_CHECKMULTISIGVERIFY = 0xaf,",
        "",
        "  // expansion",
        "  OP_NOP1 = 0xb0,",
        "  OP_CHECKLOCKTIMEVERIFY = 0xb1,",
        "  OP_NOP2 = OP_CHECKLOCKTIMEVERIFY,",
        "  OP_CHECKSEQUENCEVERIFY = 0xb2,",
        "  OP_NOP3 = OP_CHECKSEQUENCEVERIFY,",
        "  OP_NOP4 = 0xb3,",
        "  OP_NOP5 = 0xb4,",
        "  OP_NOP6 = 0xb5,",
        "  OP_NOP7 = 0xb6,",
        "  OP_NOP8 = 0xb7,",
        "  OP_NOP9 = 0xb8,",
        "  OP_NOP10 = 0xb9,",
        "",
        "  OP_INVALIDOPCODE = 0xff,",
        "}"
      ]
    },

    {
      heading: "scriptSig y scriptPubKey: partes esenciales de todo Bitcoin Script",
      text: [
        "En la red Bitcoin, cada Bitcoin Script se divide en dos tipos de scripts, los scriptSig y scriptPubKey.",
        "El primero, el scriptSig, es el script de desbloqueo, que requiere de una clave pública y una firma digital. De hecho, luego de detectar varios problemas en las primeras versiones del software de Bitcoin, se incluyeron las verificaciones de las firmas. Por lo que el sistema solo acepta realizar transacciones si las firmas y su verificación cumplen con una serie de reglas establecidas que garantizan un comportamiento adecuado en la red.",
        "El segundo, el scriptPubKey, es el script de bloqueo, que contiene un hash de clave pública, también denominada dirección de Bitcoin.",
        "Algunos scripts de Bitcoin requieren de multifirmas, es decir, de la autorización de varios usuarios para realizar la transacción. En este caso, el script es más complicado debido a que se trata de una operación mucho más grande que la estándar entre iguales. De hecho, la programación como tal de las transacciones de Bitcoin es almacenada en esta parte del script."
      ]
    },

    {
      heading: "Ejemplo de un Bitcoin Script (estructura de la transacción)",
      text: [
        "Veamos un ejemplo práctico de cómo se ve un Bitcoin Script completo. En la siguiente tabla se muestra cómo se organiza la información en las distintas secciones del script de una transacción típica."
      ],
      table: {
        columns: ["SECCIONES", "CONTENIDO DEL BITCOIN SCRIPT"],
        rows: [
          {
            "SECCIONES": "scriptPubKey",
            "CONTENIDO DEL BITCOIN SCRIPT":
              "OP_DUP OP_HASH160 340cfcffe029e6935f4e4e5839a2fff5f29c7a571 OP_EQUALVERIFY OP_CHECKSIG"
          },
          {
            "SECCIONES": "scriptSig (Signature)",
            "CONTENIDO DEL BITCOIN SCRIPT":
              "30440220694ff325724a4f4b0f3f0c36bf8e94cac58ad7c9b4d5bd8c7286c0da623f0b\n2c02206ae94680a8f31f30cd846da258e919c94afe2dd629bf4f4ce11bbe8165ff99a5f01"
          },
          {
            "SECCIONES": "scriptSig (Pub Key)",
            "CONTENIDO DEL BITCOIN SCRIPT":
              "04fc60372d27b067ca306ba812ced9c8cd69296b83a40b9b57c593258c1b9e0\nee1c0c621ca558b878395f9645a4b67a96e51843e9c060d43a3833fdd29a91f4f31"
          }
        ]
      }
    },

    {
      heading: "Cómo se interpreta el ejemplo de Bitcoin Script",
      text: [
        "El Bitcoin Script anterior está delimitado en dos partes principales: scriptPubKey y scriptSig.",
        "En el scriptPubKey están las acciones a realizarse (las condiciones de bloqueo), mientras que en el scriptSig se incluye la firma y la clave pública que permiten verificar la autenticidad de tales acciones. De esta forma se asegura que solo quien tiene acceso a los fondos pueda realizar alguna operación con los mismos.",
        "Los OP_CODES que aparecen en el scriptPubKey del ejemplo son los siguientes:"
      ],
      list: [
        "OP_DUP: Duplica el elemento de la pila superior.",
        "OP_HASH160: La entrada se codifica dos veces: primero con SHA-256 y luego con RIPEMD-160.",
        "OP_EQUALVERIFY: Verifica que los datos introducidos (por ejemplo, el hash de la clave pública) sean correctos y válidos. Combina OP_EQUAL y OP_VERIFY.",
        "OP_CHECKSIG: Las salidas, entradas y el script de toda la transacción se resumen en un hash. La firma utilizada debe ser una firma válida para este hash y debe estar junto a la clave pública. Con la clave pública se utiliza un algoritmo matemático sobre la firma para comprobar que fue generada con la clave privada correspondiente."
      ],
      notes: [
        "Con esta combinación de scriptPubKey (condiciones de bloqueo) y scriptSig (datos de desbloqueo) se consigue que solo el poseedor de la clave privada pueda gastar los bitcoins asociados a esa salida."
      ]
    }
  ]
};

export default CAP_BITCOIN_SCRIPT;
